import { useQuery, useQueryClient } from '@tanstack/react-query'
import { handleResponse } from '../handleResponse.ts'
import { toLabelAddress } from '../../backend/helpers/labelAddress.ts'

// Bitquery's labels for addresses (POST /api/labels/metadata). Each address
// is cached under its own key, so labels fetched in one batch for the top
// addresses show up in every place that address appears.

export type MetadataLabel = { type: string, value: string }

// handleResponse unwraps the `{ data }` envelope
const fetchMetadataLabels = async (network: string, addresses: string[]) =>
    handleResponse<Record<string, MetadataLabel[]>>(await fetch('/api/labels/metadata', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ network, addresses }),
    }))

const labelsKey = (network: string | undefined, address: string) =>
    ['metadataLabels', network, toLabelAddress(address)]

// Labels of one address: from the cache, or fetched when `fetch()` is called
export const useMetadataLabels = (network: string | undefined, address: string) => {
    const query = useQuery({
        queryKey: labelsKey(network, address),
        queryFn: async () => (await fetchMetadataLabels(network!, [toLabelAddress(address)]))[toLabelAddress(address)] ?? [],
        enabled: false,
        retry: false,
    })
    return { ...query, fetch: () => query.refetch() }
}

// Up to 100 addresses at once (the endpoint's limit), as one request: the
// user's Bitquery token allows one request at a time
const BATCH = 100

// Prefetches labels of several addresses in one request
export const useMetadataLabelsBatch = (network: string | undefined, addresses: string[]) => {
    const queryClient = useQueryClient()
    const unique = [...new Set(addresses.map(toLabelAddress))]
    const missing = unique.filter((address) => queryClient.getQueryData(labelsKey(network, address)) === undefined)
        .slice(0, BATCH)
        .sort()

    return useQuery({
        queryKey: ['metadataLabelsBatch', network, missing.join(',')],
        enabled: !!network && missing.length > 0,
        retry: false,
        queryFn: async () => {
            const labels = await fetchMetadataLabels(network!, missing)
            for (const address of missing) {
                queryClient.setQueryData(labelsKey(network, address), labels[address] ?? [])
            }
            return true
        },
    })
}
